import React, { PureComponent, type ReactElement } from 'react';
import { Formik, FormikHelpers } from 'formik';
import * as Sentry from '@sentry/browser';

import {
  checkIsAvailableCountyCode,
  getCountryCodeByIp,
} from '@pushwoosh/geo-ip';

import { Benefits } from './components/benefits';
import {
  Form,
  FormName,
  FormValues,
  getValidationSchema,
} from './components/form';
import { Description, H1, H2 } from './ui/typography';
import { mixpanelEvent } from './mixpanel';
import { getContentByLanguage } from './components/language';
import { SuccessSend } from './components/SuccessSend';
import { CustomerLogos } from './components/CustomerLogos';

import {
  RootBox,
  RootContent,
  RootForm,
  RootLayout,
  RootWrapper,
  Wrapper,
} from './root.styles';
import {
  RootProps,
  RootState,
} from './root.types';

export class Root extends PureComponent<RootProps, RootState> {
  constructor(props: RootProps) {
    super(props);

    this.state = {
      isLoadedCountry: false,
      findCountryCode: '',
      isSubmitted: false,
      isCaughtError: false,
    };
  }

  componentDidMount(): void {
    getCountryCodeByIp()
      .then((countryCode) => {
        if (checkIsAvailableCountyCode(countryCode)) {
          this.setState({
            findCountryCode: countryCode,
          });
        }
      })
      .finally(() => {
        this.setState({
          isLoadedCountry: true,
        });
      });
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    Sentry.captureException({
      ...error,
      errorInfo,
    });

    this.setState({
      isCaughtError: true,
    });
  }

  registerInPushwoosh = async (formValues: FormValues): Promise<void> => {
    const { source } = this.props;

    const resp = await fetch('https://user-api.svc-nue.pushwoosh.com/lead-capture', {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify({
        email: formValues[FormName.EMAIL],
        firstName: formValues[FormName.FIRST_NAME],
        lastName: formValues[FormName.LAST_NAME],
        source,
        phone: formValues[FormName.PHONE],
        company: formValues[FormName.COMPANY],
        mau: formValues[FormName.MAU],
        country: formValues[FormName.COUNTRY],
        message: formValues[FormName.MESSAGE],
        language: navigator.language,
        timezoneOffset: new Date().getTimezoneOffset() * 60,
        recaptchaResponse: formValues[FormName.RECAPTCHA_RESPONSE],
      }),
    });

    if (!resp.ok) {
      const body = await resp.json();
      throw new Error(body.error);
    }
  };

  trackGtmEvent = (): void => {
    const { source } = this.props;
    const { dataLayer } = (window as any);

    if (dataLayer) {
      dataLayer.push({ event: 'lead-submitted', landingSource: source });
    }
  };

  handleSubmit = (formValues: FormValues, helpers: FormikHelpers<FormValues>): void => {
    helpers.setSubmitting(true);

    Promise.all([
      this.registerInPushwoosh(formValues),
    ])
      .then(() => {
        if (formValues[FormName.EMAIL]) {
          mixpanelEvent.identifyMixpanelUser(formValues[FormName.EMAIL]);
        }
        mixpanelEvent.track({ name: 'DemoRequest', props: { attribute: 'demoRequestForm' } });
        this.trackGtmEvent();
        this.setState({
          isSubmitted: true,
        });
      })
      .catch((e) => {
        Sentry.captureException(e);
      })
      .finally(() => {
        helpers.setSubmitting(false);
      });
  };

  render(): ReactElement | null {
    const {
      source,
      languageCode,
      hidePhone,
    } = this.props;

    const {
      TitlePage,
      desriptionPage,
      benefitsList,
      formFields,
    } = getContentByLanguage(languageCode);

    const {
      isSubmitted,
      isCaughtError,
      isLoadedCountry,
      findCountryCode,
    } = this.state;

    if (!isLoadedCountry || isCaughtError) {
      return null;
    }

    return (
      <RootBox>
        {isSubmitted ? (
          <SuccessSend languageCode={languageCode} />
        ) : (
          <RootLayout>
            <RootContent>
              <RootWrapper>
                <H1>
                  <TitlePage />
                </H1>
                <Description>
                  {desriptionPage}
                </Description>
                <Benefits benefits={benefitsList} />
              </RootWrapper>
            </RootContent>
            <RootForm>
              <Wrapper>
                <H2>{formFields.titleForm}</H2>
                <Formik
                  initialValues={{
                    [FormName.FIRST_NAME]: '',
                    [FormName.LAST_NAME]: '',
                    [FormName.EMAIL]: '',
                    [FormName.PHONE]: '',
                    [FormName.COMPANY]: '',
                    [FormName.MAU]: '',
                    [FormName.COUNTRY]: findCountryCode,
                    [FormName.MESSAGE]: '',
                    [FormName.ACCEPT_PRIVACY_POLICY]: false,
                    [FormName.ACCEPT_EDUCATION_CONTENT]: true,
                    [FormName.RECAPTCHA_RESPONSE]: '',
                  }}
                  validationSchema={getValidationSchema(hidePhone)}
                  onSubmit={this.handleSubmit}
                >
                  {(form): ReactElement => (
                    <Form
                      form={form}
                      source={source}
                      formFields={formFields}
                      hidePhone={hidePhone}
                    />
                  )}
                </Formik>
              </Wrapper>
            </RootForm>
            <CustomerLogos />
          </RootLayout>
        )}
      </RootBox>
    );
  }
}
